import React from 'react';
import { EmailBlock, ButtonBlockContent } from '../../types';
import { MERGE_TAGS } from '../../utils/mergeTagHelper';
import { MentionAutocomplete } from '../MentionAutocomplete';

interface ButtonPropertiesProps {
  block: EmailBlock;
  onUpdate: (updates: Partial<ButtonBlockContent>) => void;
  mergeTags?: Array<{ name: string; value: string; sample: string }>;
  isDarkMode: boolean;
}

export const ButtonProperties: React.FC<ButtonPropertiesProps> = ({
  block,
  onUpdate,
  mergeTags = MERGE_TAGS,
  isDarkMode
}) => {
  const content = block.content as ButtonBlockContent;

  return (
    <div className="space-y-4">
      <div>
        <label className={`
          block text-sm font-medium mb-1
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Button Text
        </label>
        <MentionAutocomplete
          value={content.text || ''}
          onChange={(value) => onUpdate({ text: value })}
          placeholder="Click here (Type @ to insert placeholders)"
          isDarkMode={isDarkMode}
          multiline={false}
        />
        <div className="mt-2">
          <p className={`
            text-xs
            ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
          `}>
            💡 Type <code className="px-1 py-0.5 rounded bg-gray-200 dark:bg-gray-700">@</code> to insert placeholders
          </p>
        </div>
      </div>

      <div>
        <label className={`
          block text-sm font-medium mb-1
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Link URL
        </label>
        <input
          type="url"
          value={content.link || ''}
          onChange={(e) => onUpdate({ link: e.target.value })}
          className={`
            w-full px-3 py-2 border rounded-md
            ${isDarkMode 
              ? 'bg-gray-700 border-gray-600 text-white' 
              : 'bg-white border-gray-300 text-gray-900'
            }
          `}
          placeholder="https://example.com"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={`
            block text-sm font-medium mb-1
            ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
          `}>
            Background Color
          </label>
          <input
            type="color"
            value={content.backgroundColor || '#3b82f6'}
            onChange={(e) => onUpdate({ backgroundColor: e.target.value })}
            className="w-full h-10 border rounded-md"
          />
        </div>

        <div>
          <label className={`
            block text-sm font-medium mb-1
            ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
          `}>
            Text Color
          </label>
          <input
            type="color"
            value={content.textColor || '#ffffff'}
            onChange={(e) => onUpdate({ textColor: e.target.value })}
            className="w-full h-10 border rounded-md"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={`
            block text-sm font-medium mb-1
            ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
          `}>
            Font Size
          </label>
          <input
            type="number"
            value={content.fontSize || 16}
            onChange={(e) => onUpdate({ fontSize: parseInt(e.target.value) || 16 })}
            className={`
              w-full px-3 py-2 border rounded-md
              ${isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
              }
            `}
          />
        </div>

        <div>
          <label className={`
            block text-sm font-medium mb-1
            ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
          `}>
            Padding
          </label>
          <input
            type="text"
            value={content.padding || '12px 24px'}
            onChange={(e) => onUpdate({ padding: e.target.value })}
            className={`
              w-full px-3 py-2 border rounded-md
              ${isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
              }
            `}
            placeholder="12px 24px"
          />
        </div>
      </div>

      <div>
        <label className={`
          block text-sm font-medium mb-1
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Border Radius
        </label>
        <input
          type="text"
          value={content.borderRadius || '4px'}
          onChange={(e) => onUpdate({ borderRadius: e.target.value })}
          className={`
            w-full px-3 py-2 border rounded-md
            ${isDarkMode 
              ? 'bg-gray-700 border-gray-600 text-white' 
              : 'bg-white border-gray-300 text-gray-900'
            }
          `}
          placeholder="4px"
        />
      </div>

      <div>
        <label className={`
          block text-sm font-medium mb-1
          ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}
        `}>
          Alignment
        </label>
        <select
          value={content.align || 'center'}
          onChange={(e) => onUpdate({ align: e.target.value as any })}
          className={`
            w-full px-3 py-2 border rounded-md
            ${isDarkMode 
              ? 'bg-gray-700 border-gray-600 text-white' 
              : 'bg-white border-gray-300 text-gray-900'
            }
          `}
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
        </select>
      </div>
    </div>
  );
};
